import { z } from "zod";
import { llmProviderSchema } from "./admin-ai-provider.js";

/**
 * Shared DTO cho Admin AI eval-gate & model/prompt catalog (US-AE8.F3-0027):
 *  - @UC9072 ai-eval-gate            — GET /admin/ai/eval-runs (list + intent%/amount%/safety + fails).
 *  - @UC9074 ai-model-prompt-catalog — GET /admin/ai/model-config (active + lịch sử) ·
 *    POST /admin/ai/model-config (đề xuất draft) · POST /admin/ai/model-config/:id/approve (4-eyes).
 *
 * Một nguồn sự thật shape cho api (S01) + admin web (S03) + eval runner (S06) — tránh drift (DRY).
 *
 * BẢO MẬT / BẤT BIẾN (invariants):
 * - % (độ chính xác) = BASIS-POINTS số nguyên [0..10000] (9700 = 97.00%) — KHÔNG float. Đây là giá
 *   trị QUYẾT ĐỊNH pass/fail của gate (money-adjacent) → phải chính xác tuyệt đối.
 * - temperature = ×100 số nguyên [0..200] (no float: 20 = 0.20); maxTokens > 0.
 * - `.strict()` — KHÔNG khai báo field lạ; reject tại biên (chống leak).
 * - 4-eyes (approver ≠ proposer) + eval-gate (chỉ approve khi run PASS) ENFORCE ở S01 (server),
 *   contract chỉ mô tả shape.
 * - modelId / systemPromptVersion KHÔNG chứa secret/API key (G4).
 * - Cursor opaque string — đối xứng common/pagination/cursor.
 */

/** Max page-size cho list eval-runs. Default (20) do backend áp khi vắng. */
export const ADMIN_AI_EVAL_MAX_LIMIT = 100;

/** Basis-points: 100.00% = 10000. */
export const AI_ACCURACY_BPS_MIN = 0;
export const AI_ACCURACY_BPS_MAX = 10_000;

/** Ngưỡng eval ĐỀ XUẤT (docs/ai-first/04 — CHƯA PO chốt): intent ≥ 97%, amount ≥ 99%, safety = 0. */
export const AI_EVAL_INTENT_THRESHOLD_BPS_DEFAULT = 9_700;
export const AI_EVAL_AMOUNT_THRESHOLD_BPS_DEFAULT = 9_900;

/** Guardrail model-config (v1 — config-driven ở service; shape range ở contract). */
export const AI_TEMPERATURE_X100_MIN = 0;
export const AI_TEMPERATURE_X100_MAX = 200;
export const AI_MAX_TOKENS_MIN = 1;
export const AI_MAX_TOKENS_MAX = 200_000;
/** modelId / promptVersion không rỗng, độ dài giới hạn (chống payload lạ). */
export const AI_MODEL_ID_MAX_LEN = 120;
export const AI_PROMPT_VERSION_MAX_LEN = 60;

// ── eval-run ─────────────────────────────────────────────────────────────────────────────────

/** Trạng thái một eval run so ngưỡng + safety: pass | fail. */
export const aiEvalStatusSchema = z.enum(["pass", "fail"]);
export type AiEvalStatus = z.infer<typeof aiEvalStatusSchema>;

/** Nguồn kích một run: ci (pipeline AE10) hoặc portal (chạy tay từ admin). */
export const aiEvalTriggerSchema = z.enum(["ci", "portal"]);
export type AiEvalTrigger = z.infer<typeof aiEvalTriggerSchema>;

/** Một case FAIL trong run (trace để portal hiện). KHÔNG chứa PII/secret. */
export const aiEvalFailCaseSchema = z
  .object({
    /** id/khoá case trong fixture. */
    caseId: z.string(),
    /** trường sai: 'intent' | 'amount' | 'safety' | ... */
    field: z.string(),
    /** input người dùng (fixture — không PII thật). */
    input: z.string(),
    /** kỳ vọng (string hoá). */
    expected: z.string(),
    /** kết quả nhận được (string hoá). */
    got: z.string(),
  })
  .strict();
export type AiEvalFailCase = z.infer<typeof aiEvalFailCaseSchema>;

/** Một dòng eval run trong list (không kèm fails đầy đủ nếu list gọn — nhưng ở đây trả luôn count). */
export const aiEvalRunListItemSchema = z
  .object({
    runId: z.string(),
    suite: z.string(),
    model: z.string(),
    promptVersion: z.string(),
    total: z.number().int().min(0),
    passed: z.number().int().min(0),
    /** basis-points [0..10000] — no float. */
    intentAccuracyBps: z.number().int().min(AI_ACCURACY_BPS_MIN).max(AI_ACCURACY_BPS_MAX),
    amountAccuracyBps: z.number().int().min(AI_ACCURACY_BPS_MIN).max(AI_ACCURACY_BPS_MAX),
    safetyViolations: z.number().int().min(0),
    status: aiEvalStatusSchema,
    triggeredBy: aiEvalTriggerSchema,
    /** số case fail (chi tiết trace ở detail/fails). */
    failCount: z.number().int().min(0),
    ranAt: z.string(),
  })
  .strict();
export type AiEvalRunListItem = z.infer<typeof aiEvalRunListItemSchema>;

/** Detail một run: kèm danh sách case fail (trace). */
export const aiEvalRunDetailSchema = aiEvalRunListItemSchema
  .extend({
    fails: z.array(aiEvalFailCaseSchema),
  })
  .strict();
export type AiEvalRunDetail = z.infer<typeof aiEvalRunDetailSchema>;

/** Envelope list eval-runs (cursor pagination) — đối xứng repo {data, pageInfo}. */
export const aiEvalRunListResponseSchema = z
  .object({
    data: z.array(aiEvalRunDetailSchema),
    /** ngưỡng hiệu lực (config-driven) để UI hiển thị đạt/trượt so với ngưỡng nào. */
    thresholds: z
      .object({
        intentBps: z.number().int().min(AI_ACCURACY_BPS_MIN).max(AI_ACCURACY_BPS_MAX),
        amountBps: z.number().int().min(AI_ACCURACY_BPS_MIN).max(AI_ACCURACY_BPS_MAX),
      })
      .strict(),
    pageInfo: z
      .object({
        hasNext: z.boolean(),
        nextCursor: z.string().nullable(),
      })
      .strict(),
  })
  .strict();
export type AiEvalRunListResponse = z.infer<typeof aiEvalRunListResponseSchema>;

// ── model-config catalog ───────────────────────────────────────────────────────────────────────

/** Trạng thái một đề xuất config: draft | approved | rejected. */
export const aiModelConfigStatusSchema = z.enum(["draft", "approved", "rejected"]);
export type AiModelConfigStatus = z.infer<typeof aiModelConfigStatusSchema>;

/** Một bản ghi model-config trong catalog. temperature ×100 (no float). */
export const aiModelConfigItemSchema = z
  .object({
    id: z.string(),
    modelId: z.string(),
    /** Nhà cung cấp LLM (US-AE-IMP.F6-0118 · @UC9148) — anthropic|google|openai. */
    provider: llmProviderSchema,
    systemPromptVersion: z.string(),
    /** ×100 số nguyên [0..200] (20 = 0.20). */
    temperatureX100: z.number().int().min(AI_TEMPERATURE_X100_MIN).max(AI_TEMPERATURE_X100_MAX),
    maxTokens: z.number().int().min(AI_MAX_TOKENS_MIN).max(AI_MAX_TOKENS_MAX),
    status: aiModelConfigStatusSchema,
    createdBy: z.string(),
    approvedBy: z.string().nullable(),
    /** run eval đã PASS (gate) — null khi draft. */
    evalRunId: z.string().nullable(),
    createdAt: z.string(),
    approvedAt: z.string().nullable(),
  })
  .strict();
export type AiModelConfigItem = z.infer<typeof aiModelConfigItemSchema>;

/** GET /admin/ai/model-config — config active hiện hành + lịch sử. */
export const getAiModelConfigResponseSchema = z
  .object({
    /** config approved mới nhất (active) — null nếu chưa có. */
    active: aiModelConfigItemSchema.nullable(),
    /** toàn bộ lịch sử (draft/approved/rejected), mới → cũ. */
    history: z.array(aiModelConfigItemSchema),
  })
  .strict();
export type GetAiModelConfigResponse = z.infer<typeof getAiModelConfigResponseSchema>;

/**
 * POST /admin/ai/model-config — đề xuất (draft) đổi model/prompt/tham số. `.strict()` chặn key lạ.
 * temperature ×100 (no float), maxTokens range. modelId/promptVersion không rỗng, giới hạn độ dài.
 */
export const proposeAiModelConfigRequestSchema = z
  .object({
    modelId: z.string().min(1).max(AI_MODEL_ID_MAX_LEN),
    /**
     * Nhà cung cấp (US-AE-IMP.F6-0118 · @UC9148). OPTIONAL để tương thích ngược đề xuất cũ (admin chưa
     * gửi provider — task 0119 UI thêm chọn provider). Vắng ⇒ DB cột `provider` default 'anthropic'.
     * Router S05 fail-closed nếu provider chưa có adapter.
     */
    provider: llmProviderSchema.optional(),
    systemPromptVersion: z.string().min(1).max(AI_PROMPT_VERSION_MAX_LEN),
    temperatureX100: z.number().int().min(AI_TEMPERATURE_X100_MIN).max(AI_TEMPERATURE_X100_MAX),
    maxTokens: z.number().int().min(AI_MAX_TOKENS_MIN).max(AI_MAX_TOKENS_MAX),
  })
  .strict();
export type ProposeAiModelConfigRequest = z.infer<typeof proposeAiModelConfigRequestSchema>;

/**
 * POST /admin/ai/model-config/:id/approve — kích hoạt (activate) một draft. Body mang evalRunId
 * (run PASS làm gate). Server enforce: approver ≠ creator (4-eyes) + run PASS (eval-gate). `.strict()`.
 */
export const approveAiModelConfigRequestSchema = z
  .object({
    /** run eval đã PASS dùng làm gate cho activate. */
    evalRunId: z.string().min(1),
  })
  .strict();
export type ApproveAiModelConfigRequest = z.infer<typeof approveAiModelConfigRequestSchema>;
